import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getCodeGroups,
  getCodes,
  upsertCodeGroup,
  deleteCodeGroup,
  upsertCode,
  deleteCode,
} from '../../lib/api/admin-code-api';
import './CodeManagement.css';

const emptyGroupDraft = () => ({ groupId: null, groupCode: '', groupName: '', description: '', isActive: true });
const emptyCodeDraft = (groupId) => ({
  codeId: null, groupId, code: '', codeName: '', codeValue: '', description: '', sortOrder: 0, isActive: true,
});

function CodeManagement() {
  const { token } = useAuth();

  const [groups, setGroups] = useState([]);
  const [codes, setCodes] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [error, setError] = useState('');
  const [groupDraft, setGroupDraft] = useState(null);
  const [codeDraft, setCodeDraft] = useState(null);

  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    setError('');
    try {
      setGroups(await getCodeGroups(token));
    } catch (e) {
      setError(e?.message || '코드 그룹 조회에 실패했습니다.');
    } finally {
      setLoadingGroups(false);
    }
  }, [token]);

  const loadCodes = useCallback(async (groupId) => {
    if (!groupId) { setCodes([]); return; }
    setLoadingCodes(true);
    try {
      setCodes(await getCodes(token, groupId));
    } catch (e) {
      setError(e?.message || '코드 조회에 실패했습니다.');
      setCodes([]);
    } finally {
      setLoadingCodes(false);
    }
  }, [token]);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const handleSelectGroup = (groupId) => {
    setSelectedGroupId(groupId);
    setCodeDraft(null);
    loadCodes(groupId);
  };

  // ===== 그룹 저장/삭제 =====
  const saveGroup = async () => {
    if (!groupDraft) return;
    const { ok, message } = await upsertCodeGroup(token, groupDraft);
    if (!ok) { alert(message || '저장에 실패했습니다.'); return; }
    setGroupDraft(null);
    await loadGroups();
  };

  const removeGroup = async (groupId) => {
    if (!window.confirm('이 그룹을 삭제하시겠습니까? (하위 코드도 영향받을 수 있습니다)')) return;
    const { ok, message } = await deleteCodeGroup(token, groupId);
    if (!ok) { alert(message || '삭제에 실패했습니다.'); return; }
    if (selectedGroupId === groupId) { setSelectedGroupId(null); setCodes([]); }
    await loadGroups();
  };

  // ===== 코드 저장/삭제 =====
  const saveCode = async () => {
    if (!codeDraft) return;
    const { ok, message } = await upsertCode(token, codeDraft);
    if (!ok) { alert(message || '저장에 실패했습니다.'); return; }
    setCodeDraft(null);
    await loadCodes(selectedGroupId);
  };

  const removeCode = async (codeId) => {
    if (!window.confirm('이 코드를 삭제하시겠습니까?')) return;
    const { ok, message } = await deleteCode(token, codeId);
    if (!ok) { alert(message || '삭제에 실패했습니다.'); return; }
    await loadCodes(selectedGroupId);
  };

  const renderActiveCell = (isActive) => (isActive ? 'Y' : 'N');

  return (
    <div className="code-management">
      {!!error && <div className="admin-table-status error">{error}</div>}

      <div className="code-mgmt-split">
        {/* 좌측: 코드 그룹 */}
        <section className="code-mgmt-panel">
          <div className="code-mgmt-panel-head">
            <h3>코드 그룹</h3>
            <button type="button" className="code-mgmt-btn primary" onClick={() => setGroupDraft(emptyGroupDraft())}>
              그룹 추가
            </button>
          </div>
          {loadingGroups && <div className="admin-table-status">불러오는 중입니다...</div>}
          <table className="admin-basic-table code-mgmt-table">
            <thead>
              <tr>
                <th style={{ width: 120 }}>그룹코드</th>
                <th>그룹명</th>
                <th>설명</th>
                <th style={{ width: 56 }}>활성</th>
                <th style={{ width: 110 }}>관리</th>
              </tr>
            </thead>
            <tbody>
              {groupDraft && !groupDraft.groupId && (
                <GroupEditRow draft={groupDraft} setDraft={setGroupDraft} onSave={saveGroup} onCancel={() => setGroupDraft(null)} />
              )}
              {groups.map((row) => (
                groupDraft && groupDraft.groupId === row.groupId ? (
                  <GroupEditRow key={row.groupId} draft={groupDraft} setDraft={setGroupDraft} onSave={saveGroup} onCancel={() => setGroupDraft(null)} />
                ) : (
                  <tr
                    key={row.groupId}
                    className={selectedGroupId === row.groupId ? 'selected' : ''}
                    onClick={() => handleSelectGroup(row.groupId)}
                  >
                    <td>{row.groupCode}</td>
                    <td>{row.groupName}</td>
                    <td className="muted">{row.description || '-'}</td>
                    <td>{renderActiveCell(row.isActive)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button type="button" className="code-mgmt-btn" onClick={() => setGroupDraft({ ...row })}>수정</button>
                      <button type="button" className="code-mgmt-btn danger" onClick={() => removeGroup(row.groupId)}>삭제</button>
                    </td>
                  </tr>
                )
              ))}
              {!loadingGroups && groups.length === 0 && !groupDraft && (
                <tr><td colSpan={5} className="muted center">등록된 코드 그룹이 없습니다.</td></tr>
              )}
            </tbody>
          </table>
        </section>

        {/* 우측: 코드 상세 */}
        <section className="code-mgmt-panel">
          <div className="code-mgmt-panel-head">
            <h3>코드 상세{selectedGroupId ? '' : ' (그룹을 선택하세요)'}</h3>
            <button
              type="button"
              className="code-mgmt-btn primary"
              disabled={!selectedGroupId}
              onClick={() => setCodeDraft(emptyCodeDraft(selectedGroupId))}
            >
              코드 추가
            </button>
          </div>
          {loadingCodes && <div className="admin-table-status">불러오는 중입니다...</div>}
          <table className="admin-basic-table code-mgmt-table">
            <thead>
              <tr>
                <th style={{ width: 110 }}>코드</th>
                <th>코드명</th>
                <th style={{ width: 110 }}>코드값</th>
                <th>설명</th>
                <th style={{ width: 56 }}>정렬</th>
                <th style={{ width: 56 }}>활성</th>
                <th style={{ width: 110 }}>관리</th>
              </tr>
            </thead>
            <tbody>
              {codeDraft && !codeDraft.codeId && (
                <CodeEditRow draft={codeDraft} setDraft={setCodeDraft} onSave={saveCode} onCancel={() => setCodeDraft(null)} />
              )}
              {codes.map((row) => (
                codeDraft && codeDraft.codeId === row.codeId ? (
                  <CodeEditRow key={row.codeId} draft={codeDraft} setDraft={setCodeDraft} onSave={saveCode} onCancel={() => setCodeDraft(null)} />
                ) : (
                  <tr key={row.codeId}>
                    <td>{row.code}</td>
                    <td>{row.codeName}</td>
                    <td className="muted">{row.codeValue || '-'}</td>
                    <td className="muted">{row.description || '-'}</td>
                    <td>{row.sortOrder}</td>
                    <td>{renderActiveCell(row.isActive)}</td>
                    <td>
                      <button type="button" className="code-mgmt-btn" onClick={() => setCodeDraft({ ...row })}>수정</button>
                      <button type="button" className="code-mgmt-btn danger" onClick={() => removeCode(row.codeId)}>삭제</button>
                    </td>
                  </tr>
                )
              ))}
              {selectedGroupId && !loadingCodes && codes.length === 0 && !codeDraft && (
                <tr><td colSpan={7} className="muted center">등록된 코드가 없습니다.</td></tr>
              )}
              {!selectedGroupId && (
                <tr><td colSpan={7} className="muted center">좌측에서 코드 그룹을 선택하세요.</td></tr>
              )}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}

function GroupEditRow({ draft, setDraft, onSave, onCancel }) {
  const set = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));
  const onKey = (e) => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') onCancel(); };
  return (
    <tr className="editing" onClick={(e) => e.stopPropagation()}>
      <td><input value={draft.groupCode} onChange={(e) => set('groupCode', e.target.value)} onKeyDown={onKey} placeholder="그룹코드*" /></td>
      <td><input value={draft.groupName} onChange={(e) => set('groupName', e.target.value)} onKeyDown={onKey} placeholder="그룹명*" /></td>
      <td><input value={draft.description || ''} onChange={(e) => set('description', e.target.value)} onKeyDown={onKey} placeholder="설명" /></td>
      <td><input type="checkbox" checked={!!draft.isActive} onChange={(e) => set('isActive', e.target.checked)} /></td>
      <td>
        <button type="button" className="code-mgmt-btn primary" onClick={onSave}>저장</button>
        <button type="button" className="code-mgmt-btn" onClick={onCancel}>취소</button>
      </td>
    </tr>
  );
}

function CodeEditRow({ draft, setDraft, onSave, onCancel }) {
  const set = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));
  const onKey = (e) => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') onCancel(); };
  return (
    <tr className="editing">
      <td><input value={draft.code} onChange={(e) => set('code', e.target.value)} onKeyDown={onKey} placeholder="코드*" /></td>
      <td><input value={draft.codeName} onChange={(e) => set('codeName', e.target.value)} onKeyDown={onKey} placeholder="코드명*" /></td>
      <td><input value={draft.codeValue || ''} onChange={(e) => set('codeValue', e.target.value)} onKeyDown={onKey} placeholder="코드값" /></td>
      <td><input value={draft.description || ''} onChange={(e) => set('description', e.target.value)} onKeyDown={onKey} placeholder="설명" /></td>
      <td><input type="number" value={draft.sortOrder ?? 0} onChange={(e) => set('sortOrder', Number(e.target.value) || 0)} onKeyDown={onKey} /></td>
      <td><input type="checkbox" checked={!!draft.isActive} onChange={(e) => set('isActive', e.target.checked)} /></td>
      <td>
        <button type="button" className="code-mgmt-btn primary" onClick={onSave}>저장</button>
        <button type="button" className="code-mgmt-btn" onClick={onCancel}>취소</button>
      </td>
    </tr>
  );
}

export default CodeManagement;
